import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Gallery from 'components/gallery';
import BackgroundImage from 'gatsby-background-image';
import useBackgrounds from 'modules/useBackgrounds';
import { useMediaQuery } from 'react-responsive';
import './panel.scss';

const Panel = ({
  layout,
  title,
  images,
  children,
  background,
  mobileBackground,
  icon,
  tag,
  anchor,
  style,
  mobileStyle,
  dial,
  footer,
}) => {
  const defaultStyle = {
    paddingTop: 250,
    paddingBottom: 250,
  };
  const defaultMobileStyle = {
    paddingTop: 100,
    paddingBottom: 100,
  };
  const isMobile = useMediaQuery({ query: '(max-width: 1023px)' });
  const mergedStyle = { ...defaultStyle, ...style };
  const mergedMobileStyle = { ...defaultMobileStyle, ...style, ...mobileStyle };
  const panelChildren = (
    <>
      {anchor && <span id={anchor} className="panel__anchor" />}
      {tag && <span className="panel__tag" />}
      {icon && icon === 'key' && <span className="panel__key" />}
      {icon && icon === 'lock' && <span className="panel__lock" />}
      <h3 className="panel__title">{title}</h3>
      <div className="panel__body">
        {children}
        {images && <Gallery images={images} />}
      </div>
      {footer && <div className="panel__footer">{footer}</div>}
    </>
  );
  const classes = classnames({
    panel: true,
    [`panel--${layout}`]: true,
    'panel--key': icon && icon === 'key',
    'panel--lock': icon && icon === 'lock',
    'panel--dial': dial,
    'panel--tag': tag,
    'panel--normal': !tag && !icon,
    'panel--has-mobile-background': mobileBackground !== '',
  });

  if (background) {
    const mobileImage = useBackgrounds(mobileBackground || background);
    const image = useBackgrounds(background);
    return (
      <BackgroundImage
        Tag="section"
        className={classes}
        fluid={isMobile ? mobileImage : image}
        style={isMobile ? mergedMobileStyle : mergedStyle}
      >
        {panelChildren}
        {mobileBackground !== '' && <div className="panel__mobile-gradient" />}
      </BackgroundImage>
    );
  }
  return (
    <section
      className={classes}
      style={isMobile ? mergedMobileStyle : mergedStyle}
    >
      {panelChildren}
    </section>
  );
};

Panel.defaultProps = {
  layout: 'bottom',
  images: [],
  background: '',
  mobileBackground: '',
  tag: false,
  dial: false,
  icon: '',
  style: {},
  mobileStyle: {},
  anchor: '',
  footer: null,
};

Panel.propTypes = {
  children: PropTypes.node.isRequired,
  layout: PropTypes.oneOf(['left', 'right', 'bottom']),
  title: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(PropTypes.object),
  background: PropTypes.string,
  mobileBackground: PropTypes.string,
  tag: PropTypes.bool,
  dial: PropTypes.bool,
  icon: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  mobileStyle: PropTypes.object,
  anchor: PropTypes.string,
  footer: PropTypes.node,
};

export default Panel;
